'use client';

import { QueryCGType } from '@/types/graphql/root';
import Pin2020 from '@svg/poi-modal/pin_20_20.svg';
import Phone2020 from '@svg/poi-modal/phone_20_20.svg';
import Clock2020 from '@svg/poi-modal/clock_20_20.svg';
import Home2020 from '@svg/poi-modal/home_20_20.svg';
import Search2020 from '@svg/poi-modal/search_20_20.svg';
import MapPrimary2020 from '@svg/poi-modal/map_primary_20_20.svg';
import PastePrimary1616 from '@svg/poi-modal/paste_primary_16_16.svg';
import { useMemo } from 'react';
import { filterNullUndefined, PoiType } from '@/utils';
import copy from 'copy-to-clipboard';
import { useSetAtom } from 'jotai';
import { centerToastModalAtom } from '@/atom/modal';
import OuterSearchLink from './poi-detail-outer-search-link';
import LinkForZzimcarAndroid from '../common/link-for-zzimcar-android';

export default function PoiDetailGeneral({ poi }: { poi: QueryCGType['poi'] | null }) {
  const setCenterToastModal = useSetAtom(centerToastModalAtom);

  const generalInfo = useMemo(() => {
    const address = poi?.address;
    const googleMapUrl = poi?.placeId ? `https://google.com/maps/place/?q=place_id:${poi.placeId}` : null;
    const phone = poi?.phone;
    const openHours = poi?.poiType === PoiType.accomm ? null : filterNullUndefined(poi?.openHours);
    const website = poi?.websiteUrl;
    const outerSearchLinks = poi?.name
      ? {
          google: `https://www.google.com/search?q=${poi.name}`,
          naver: `https://search.naver.com/search.naver?query=${poi.name}`,
        }
      : null;

    return {
      address,
      googleMapUrl,
      phone,
      openHours,
      website,
      outerSearchLinks,
    };
  }, [poi]);

  if (!poi) {
    return (
      <div className='py-24pxr px-16pxr flex flex-col gap-14pxr'>
        <div className='flex items-start gap-4pxr w-full'>
          <span className='p-4pxr'>
            <Pin2020 className='flex-none' />
          </span>
          <div className='flex flex-col w-full'>
            <div className='w-full h-28pxr rounded-2pxr skeleton-item'></div>
            <div className='flex gap-12pxr py-4pxr items-center'>
              <div className='w-92pxr h-20pxr rounded-2pxr skeleton-item'></div>
              <div className='w-92pxr h-20pxr rounded-2pxr skeleton-item'></div>
            </div>
          </div>
        </div>

        <div className='flex items-start gap-4pxr w-full'>
          <span className='p-4pxr'>
            <Phone2020 className='flex-none' />
          </span>
          <div className='flex flex-col w-full'>
            <div className='w-150pxr h-28pxr rounded-2pxr skeleton-item'></div>
          </div>
        </div>

        <div className='flex items-start gap-4pxr w-full'>
          <span className='p-4pxr'>
            <Clock2020 className='flex-none' />
          </span>
          <div className='flex flex-col w-full gap-4pxr'>
            <div className='w-120pxr h-24pxr rounded-2pxr skeleton-item'></div>
            <div className='w-120pxr h-24pxr rounded-2pxr skeleton-item'></div>
            <div className='w-120pxr h-24pxr rounded-2pxr skeleton-item'></div>
            <div className='w-120pxr h-24pxr rounded-2pxr skeleton-item'></div>
            <div className='w-120pxr h-24pxr rounded-2pxr skeleton-item'></div>
            <div className='w-120pxr h-24pxr rounded-2pxr skeleton-item'></div>
            <div className='w-120pxr h-24pxr rounded-2pxr skeleton-item'></div>
          </div>
        </div>

        <div className='flex items-start gap-4pxr w-full'>
          <span className='p-4pxr'>
            <Home2020 className='flex-none' />
          </span>
          <div className='flex flex-col w-full'>
            <div className='w-220pxr h-28pxr rounded-2pxr skeleton-item'></div>
          </div>
        </div>

        <div className='flex items-start gap-4pxr w-full'>
          <span className='p-4pxr'>
            <Search2020 className='flex-none' />
          </span>
          <div className='flex flex-col w-full gap-10pxr'>
            <div className='w-80pxr h-28pxr rounded-2pxr skeleton-item'></div>
            <div className='flex gap-4pxr'>
              <div className='w-124pxr h-33pxr rounded-17pxr skeleton-item'></div>
              <div className='w-124pxr h-33pxr rounded-17pxr skeleton-item'></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div id='poi-detail-general' className='py-24pxr px-16pxr flex flex-col gap-14pxr'>
        <div className='body-14-r text-new-blue-500'>*영업정보 등 업데이트 기준에 따라 상이할 수 있습니다.</div>
        {generalInfo.address && (
          <div className='flex items-start gap-4pxr w-full'>
            <span className='p-4pxr'>
              <Pin2020 className='flex-none' />
            </span>
            <div className='flex flex-col w-full pt-2pxr'>
              <p className='text-gray-700 body-16-r'>{generalInfo.address}</p>
              <div className='flex gap-12pxr py-4pxr items-center'>
                {generalInfo.googleMapUrl && (
                  <LinkForZzimcarAndroid
                    href={generalInfo.googleMapUrl}
                    prefetch={false}
                    target='_blank'
                    rel='noreferrer'
                    className='flex flex-row items-center gap-4pxr py-4pxr text-point-400 button-14-m'
                  >
                    <MapPrimary2020 className='flex-none' />
                    구글맵 열기
                  </LinkForZzimcarAndroid>
                )}
                <button
                  className='flex flex-row items-center gap-4pxr py-4pxr text-point-400 button-14-m'
                  onClick={() => {
                    copy(generalInfo.address ?? '');
                    setCenterToastModal({
                      renderingTime: 2000,
                      text: '주소가 클립보드에\n복사되었어요',
                    });
                  }}
                >
                  <PastePrimary1616 className='flex-none m-1pxr' />
                  주소 복사하기
                </button>
              </div>
            </div>
          </div>
        )}

        {generalInfo.phone && (
          <div className='flex items-start gap-4pxr w-full'>
            <span className='p-4pxr'>
              <Phone2020 className='flex-none' />
            </span>
            <span className='text-gray-700 body-16-r h-28pxr flex items-center'>{generalInfo.phone}</span>
          </div>
        )}

        {generalInfo.openHours && generalInfo.openHours.length > 0 && (
          <div className='flex items-start gap-4pxr w-full'>
            <span className='p-4pxr'>
              <Clock2020 className='flex-none' />
            </span>
            <div className='flex flex-col text-gray-700 gap-4pxr body-16-r pt-2pxr'>
              {generalInfo.openHours.map((openHour, index) => (
                <span key={index}>{openHour}</span>
              ))}
            </div>
          </div>
        )}

        {generalInfo.website && generalInfo.website.length > 0 && (
          <div className='flex items-start gap-4pxr w-full'>
            <span className='p-4pxr'>
              <Home2020 className='flex-none' />
            </span>
            <LinkForZzimcarAndroid
              href={generalInfo.website}
              prefetch={false}
              target='_blank'
              className='text-gray-700 underline truncate body-16-r pt-2pxr'
              rel='noreferrer'
            >
              {generalInfo.website}
            </LinkForZzimcarAndroid>
          </div>
        )}

        {generalInfo.outerSearchLinks && (
          <div className='flex items-start gap-4pxr w-full'>
            <span className='p-4pxr'>
              <Search2020 className='flex-none' />
            </span>
            <div className='flex flex-col gap-10pxr pt-2pxr'>
              <span className='text-gray-700 body-16-r'>더 알아보기</span>
              <div className='flex flex-row flex-wrap gap-x-4pxr gap-y-10pxr'>
                <OuterSearchLink type='google' link={generalInfo.outerSearchLinks.google} />
                <OuterSearchLink type='naver' link={generalInfo.outerSearchLinks.naver} />
              </div>
            </div>
          </div>
        )}
      </div>
      <div className='w-full bg-gray-100 h-12pxr'></div>
    </>
  );
}
